/**
 * BuilderWorkspace — the manual editing shell around the live preview.
 *
 * Top toolbar (Back, collapse, title, Save) over a resizable/collapsible left sidebar
 * (drill-down Design + Settings) and the live preview. The preview renders the SAME
 * Builder Engine used everywhere else, fed directly by the editor context's working
 * schema — so every edit updates it live with no iframe, no second renderer, and no
 * duplicated runtime. Nothing persists until the user clicks Save (no autosave).
 *
 * A floating bottom toolbar restores the Desktop/Mobile toggle, Reload, and View live.
 * Logo size is applied as a preview-scoped CSS override so the frozen navbar component
 * and its stylesheet are never modified.
 *
 * The Back arrow is a plain one-step browser-history back (`navigate(-1)`) — never a
 * hardcoded route, so it always returns to whatever page the user actually came from
 * (normally the Report page, but never assumed).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowLeft, PanelLeftClose, PanelLeft, Loader2, Check, TriangleAlert } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { APP_NAME, BRAND } from '../../config'
import { SB_SCROLL_TO_SECTION, SB_ACTIVE_SECTION } from '../../site-builder/runtime'
import { useBuilderEditor } from './editorContext'
import { BuilderSidebar } from './BuilderSidebar'
import { PreviewToolbar } from '../PreviewToolbar'
import type { PreviewMode } from '../PreviewToolbar'

const MIN_W = 300
const MAX_W = 460

function SaveButton({ dirty, saving, onSave }: { dirty: boolean; saving: boolean; onSave: () => void }) {
  if (!dirty && !saving) {
    return (
      <span className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-emerald-600">
        <Check className="h-3.5 w-3.5" /> Saved
      </span>
    )
  }
  return (
    <button
      type="button"
      onClick={onSave}
      disabled={saving}
      className="flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors disabled:opacity-70"
      style={{ backgroundColor: BRAND.red }}
      onMouseOver={(ev) => { ev.currentTarget.style.backgroundColor = BRAND.redHover }}
      onMouseOut={(ev) => { ev.currentTarget.style.backgroundColor = BRAND.red }}
    >
      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
      {saving ? 'Saving…' : 'Save changes'}
    </button>
  )
}

export function BuilderWorkspace() {
  const { schema, themeOverrides, saveState, dirty, save, scrollRequest, setPreviewActiveSectionId } = useBuilderEditor()
  const location = useLocation()
  const navigate = useNavigate()

  const [width, setWidth] = useState(360)
  const [collapsed, setCollapsed] = useState(false)
  const [mode, setMode] = useState<PreviewMode>('desktop')
  const [reloadKey, setReloadKey] = useState(0)

  const siteName = schema.seo.title.split('·')[0].trim() || schema.seo.title
  const livePath = `${location.pathname.replace(/\/+$/, '')}/live`
  // Back arrow: a plain "one step back in browser history" — never a hardcoded route, so
  // it always returns to wherever the user actually came from (normally the Report page).
  const goBack = () => navigate(-1)

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = width
    const onMove = (ev: PointerEvent) => setWidth(Math.min(MAX_W, Math.max(MIN_W, startW + (ev.clientX - startX))))
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  // Stream the live working schema into the preview iframe so it stays live while editing
  // AND renders in a real viewport (correct responsive typography, matching /live).
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const postSchema = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'sb-preview-schema', schema, themeVars: themeOverrides },
      window.location.origin,
    )
  }, [schema, themeOverrides])

  useEffect(() => {
    postSchema()
  }, [postSchema])

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return
      const data = e.data as { type?: string; sectionId?: string } | null
      if (data?.type === 'sb-preview-ready') postSchema()
      // Section Navigation UX: the preview's own IntersectionObserver reports whichever
      // section is currently scrolled into view, so the sidebar can keep highlighting it
      // even when the user scrolls the preview manually instead of clicking a section.
      if (data?.type === SB_ACTIVE_SECTION && data.sectionId) setPreviewActiveSectionId(data.sectionId)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [postSchema, setPreviewActiveSectionId])

  // Section Navigation UX: clicking a section in the sidebar (see BuilderSidebar/
  // SectionsPanel) sets `scrollRequest` on the shared editor context; forward it to the
  // preview as a smooth-scroll instruction. A plain state watch rather than a direct call
  // from the sidebar, since the sidebar has no reason to know the preview is an iframe.
  useEffect(() => {
    if (!scrollRequest) return
    iframeRef.current?.contentWindow?.postMessage(
      { type: SB_SCROLL_TO_SECTION, sectionId: scrollRequest.sectionId },
      window.location.origin,
    )
  }, [scrollRequest])

  // Restored device frame (last-known-good): a real-viewport iframe — mobile = a narrow
  // viewport, so the site's vw units and @media breakpoints render exactly as on a real
  // device / DevTools responsive mode. No scaling, zoom, or transforms.
  const frameClass =
    mode === 'mobile'
      ? 'my-4 h-[calc(100%-2rem)] w-[410px] max-w-full rounded-[28px] shadow-2xl ring-1 ring-black/10'
      : 'h-full w-full'

  return (
    <div className="flex h-screen flex-col bg-gray-100">
      {/* Top toolbar */}
      <header className="flex items-center gap-3 border-b border-gray-200 bg-white px-3 py-2">
        <button
          type="button"
          onClick={goBack}
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label="Go back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label={collapsed ? 'Show editor panel' : 'Hide editor panel'}
          aria-pressed={!collapsed}
        >
          {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
        <p className="min-w-0 truncate text-sm font-semibold text-gray-900">{siteName}</p>
        <div className="ml-auto flex items-center gap-3">
          {saveState === 'error' ? (
            <span className="flex items-center gap-1 text-xs font-medium text-red-600">
              <TriangleAlert className="h-3.5 w-3.5" /> Couldn’t save
            </span>
          ) : null}
          <SaveButton dirty={dirty} saving={saveState === 'saving'} onSave={save} />
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-500">{APP_NAME}</span>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Left sidebar (resizable + collapsible) */}
        <motion.aside
          initial={false}
          animate={{ width: collapsed ? 0 : width }}
          transition={{ type: 'tween', duration: 0.2, ease: 'easeOut' }}
          className="relative shrink-0 overflow-hidden border-r border-gray-200 bg-white"
        >
          <div className="h-full" style={{ width }}>
            <BuilderSidebar />
          </div>
          {!collapsed ? (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize editor panel"
              onPointerDown={startResize}
              className="absolute inset-y-0 right-0 w-1.5 cursor-col-resize bg-transparent hover:bg-gray-200"
            />
          ) : null}
        </motion.aside>

        {/* Live preview — the existing Builder Engine rendered in a real-viewport iframe
            (same LivePreviewPage as /live), fed the live working schema via postMessage. */}
        <main className="flex min-w-0 flex-1 justify-center overflow-hidden bg-gray-100">
          <iframe
            ref={iframeRef}
            key={reloadKey}
            src={livePath}
            title="Website preview"
            onLoad={postSchema}
            className={`border-0 bg-white transition-[width,height,border-radius] duration-300 ease-out ${frameClass}`}
          />
        </main>
      </div>

      <PreviewToolbar
        mode={mode}
        onMode={setMode}
        onRefresh={() => setReloadKey((k) => k + 1)}
        onViewLive={() => window.open(livePath, '_blank', 'noopener,noreferrer')}
      />
    </div>
  )
}
